import { type AuthChangeEvent, type Session } from '@supabase/supabase-js'
import { supabaseClient } from '../SupabaseClient'
import { type UserMetaData } from '../../utils/User'
import { trackError } from '../analytics'

export function setUpAuth(
  onEvent: (
    event: AuthChangeEvent,
    session: Session | null
  ) => void | Promise<void>
): void {
  supabaseClient.auth.onAuthStateChange(onEvent)
}

export async function signInWithEmailOTP(email: string): Promise<boolean> {
  // console.debug('signInWithEmailOTP', email)
  const { error } = await supabaseClient.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: 'https://app.noteplan.co',
    },
  })

  if (error) {
    trackError('signInWithEmailOTP', error.message)
    return false
  }

  return true
}

export async function verifyOTP(
  email: string,
  token: string
): Promise<boolean> {
  const { error } = await supabaseClient.auth.verifyOtp({
    email,
    token,
    type: 'email',
  })

  if (error) {
    trackError('verifyOTP', error.message)
    return false
  }

  return true
}

export async function signOut(): Promise<void> {
  // clear the meta as well, when the user is logged out he doesn't have access to the meta data anyway
  // eslint-disable-next-line unicorn/no-null
  await setUserMetaData(null)

  const { error } = await supabaseClient.auth.signOut({ scope: 'local' })

  if (error) {
    trackError('signOut', error.message)
  }
}

export async function session(): Promise<Session | null> {
  const { data, error } = await supabaseClient.auth.getSession()

  if (error) {
    trackError('session', error.message)
    throw error
  }

  return data.session
}

export async function setUserMetaData(
  meta: UserMetaData | null
): Promise<boolean> {
  // Get the user id
  const { data: userData, error: userError } =
    await supabaseClient.auth.getUser()

  if (userError) {
    trackError('setUserMetaData', userError.message)
    throw userError
  }

  // Update the meta column in the user_profiles table
  const { error } = await supabaseClient
    .from('user_profiles')
    .update({ meta })
    .eq('id', userData.user.id)

  if (error) {
    trackError('setUserMetaData', error.message)
    throw new Error(error.message)
  }

  return true
}

export async function getUserMetaData(): Promise<UserMetaData | null> {
  // Get the user id
  const { data: userData, error: userError } =
    await supabaseClient.auth.getUser()

  if (userError) {
    trackError('getUserMetaData 1', userError.message)
    throw new Error(userError.message)
  }

  // Update the meta column in the user_profiles table
  const { data, error } = await supabaseClient
    .from('user_profiles')
    .select('meta')
    .eq('id', userData.user.id)

  if (error) {
    trackError('getUserMetaData 2', error.message)
    throw new Error(error.message)
  }

  // Ensure the data is of the expected type
  const metaData = data[0]?.meta as UserMetaData | null

  return metaData
}
